/**
 * Knowledge Base · one General Knowledge rule — the page a list row opens
 * (/knowledge/rules/:ruleId, or "new"). Layout follows curator's rule editor:
 * breadcrumb, title + status, the "applies to" tags and the actions on one
 * row, then labeled Title and Content fields.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Input,
  Modal,
  Skeleton,
} from '@servicecompressionllc/sc-design-system';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { MarkdownEditor } from '../../components/MarkdownEditor.js';
import { MarkdownViewer } from '../../components/MarkdownViewer.js';
import { knowledgeApi } from './api.js';
import { RuleStatusBadge } from './RulesPage.js';
import { SubjectPickerModal } from './SubjectPickerModal.js';

/**
 * Mirrors `RULE_BODY_MAX_CHARS` in sc-platform core (this app can't import
 * the API repo, so the value is hand-mirrored — if they drift, the server
 * still rejects and the error message names the field). Same ceiling as a
 * guide body: the GK bucket is relevance-ranked per search, not injected
 * wholesale, so a large entry is legitimate — the ceiling exists to bound a
 * runaway paste, with Fastify's 1MB body limit behind it.
 */
const RULE_BODY_MAX_CHARS = 500_000;

/** What RulesPage's upload flow hands over via router state. Narrowed rather
 *  than trusted: history state survives reloads and back/forward in shapes we
 *  didn't put there. */
interface ExtractedRuleState {
  filename: string;
  title: string;
  text: string;
  bucketId: string | null;
}
/** A hand-typed rule inherits the bucket page it was created from. */
const readNewRuleBucketId = (state: unknown): string | null => {
  if (typeof state !== 'object' || state === null) return null;
  const v = (state as { newRuleBucketId?: unknown }).newRuleBucketId;
  return typeof v === 'string' ? v : null;
};

const readExtractedRule = (state: unknown): ExtractedRuleState | null => {
  if (typeof state !== 'object' || state === null) return null;
  const wrapped = (state as { extractedRule?: unknown }).extractedRule;
  if (typeof wrapped !== 'object' || wrapped === null) return null;
  const r = wrapped as Record<string, unknown>;
  return typeof r.filename === 'string' && typeof r.title === 'string' && typeof r.text === 'string'
    ? {
        filename: r.filename,
        title: r.title,
        text: r.text,
        bucketId: typeof r.bucketId === 'string' ? r.bucketId : null,
      }
    : null;
};

export const RuleDetailPage = () => {
  const { ruleId = '' } = useParams();
  const creating = ruleId === 'new';
  const navigate = useNavigate();
  const location = useLocation();
  const extracted = creating ? readExtractedRule(location.state) : null;
  const newRuleBucketId = creating ? readNewRuleBucketId(location.state) : null;
  const qc = useQueryClient();
  const me = useMe();
  const canWrite = me.data ? canWriteKnowledge(me.data.user) : false;

  const rules = useQuery({
    queryKey: ['knowledge', 'rules'],
    queryFn: knowledgeApi.rules,
    enabled: !creating,
  });
  const rule = (rules.data ?? []).find((r) => r.id === ruleId);
  const subjects = useQuery({
    queryKey: ['knowledge', 'subjects'],
    queryFn: knowledgeApi.subjects,
  });
  const buckets = useQuery({ queryKey: ['knowledge', 'buckets'], queryFn: knowledgeApi.buckets });

  // Draft state — null until the user starts editing, so a background refetch
  // never clobbers what they're typing.
  const [draft, setDraft] = useState<{
    title: string;
    bodyMarkdown: string;
    subjectIds: string[];
  } | null>(
    creating
      ? {
          title: extracted?.title ?? '',
          bodyMarkdown: extracted?.text ?? '',
          subjectIds: [],
        }
      : null,
  );
  const [picking, setPicking] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['knowledge', 'rules'] });
  const saveDraftBody = async () => {
    if (!draft) throw new Error('nothing to save');
    const body = {
      title: draft.title.trim(),
      bodyMarkdown: draft.bodyMarkdown,
      subjectIds: draft.subjectIds,
    };
    const bucketId = extracted?.bucketId ?? newRuleBucketId;
    return creating
      ? knowledgeApi.createRule(bucketId != null ? { ...body, bucketId } : body)
      : knowledgeApi.updateRule(ruleId, body);
  };
  const landOn = (saved: { id: string }) => {
    setDraft(null);
    invalidate();
    if (creating) void navigate(`/knowledge/rules/${saved.id}`, { replace: true });
  };
  const save = useMutation({ mutationFn: saveDraftBody, onSuccess: landOn });
  // The editor's Publish is save-then-publish: nothing goes live unsaved.
  const publishDraft = useMutation({
    mutationFn: async () => {
      const saved = await saveDraftBody();
      return knowledgeApi.publishRule(saved.id);
    },
    onSuccess: landOn,
  });
  const publish = useMutation({
    mutationFn: () => knowledgeApi.publishRule(ruleId),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => knowledgeApi.deleteRule(ruleId),
    onSuccess: () => {
      invalidate();
      void navigate('/knowledge/rules');
    },
  });

  if (!creating && rules.isLoading) return <Skeleton className="h-64" />;
  if (!creating && !rule) {
    return <div className="text-sm text-danger">Couldn’t find this rule.</div>;
  }

  const editing = draft !== null;
  const shown = draft ?? {
    title: rule?.title ?? '',
    bodyMarkdown: rule?.bodyMarkdown ?? '',
    subjectIds: rule?.subjects.map((s) => s.id) ?? [],
  };
  const draftReady =
    shown.title.trim().length > 0 &&
    shown.bodyMarkdown.trim().length > 0 &&
    shown.bodyMarkdown.length <= RULE_BODY_MAX_CHARS;
  const nameOf = (id: string) => (subjects.data ?? []).find((s) => s.id === id)?.name ?? '…';
  const mutationError = save.error ?? publishDraft.error ?? publish.error;
  const draftBucketId = rule ? rule.bucketId : (extracted?.bucketId ?? newRuleBucketId);
  const homeBucket = (buckets.data ?? []).find((b) =>
    draftBucketId === null ? b.isDefault : b.id === draftBucketId,
  );
  const busy = save.isPending || publishDraft.isPending;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <section>
        <Breadcrumbs
          trail={[
            { label: 'General Knowledge', to: '/knowledge/rules' },
            ...(homeBucket
              ? [{ label: homeBucket.name, to: `/knowledge/buckets/${homeBucket.id}` }]
              : []),
          ]}
        />
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-bold text-ink">
            {shown.title.trim().length > 0 ? shown.title : 'New rule'}
          </h2>
          {rule ? <RuleStatusBadge rule={rule} /> : <Badge variant="warning">draft</Badge>}
        </div>
      </section>

      {extracted && (
        <Banner variant="info">
          <Banner.Description>
            This draft was extracted from {extracted.filename}. Review and trim it before
            publishing. Nothing is live until you publish, and the original file was not stored.
          </Banner.Description>
        </Banner>
      )}

      {/* tags + actions on one row — the scope is the first thing you see */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-ink-muted">Applies to:</span>
        {shown.subjectIds.length === 0 ? (
          <Badge variant="neutral">every subject</Badge>
        ) : (
          shown.subjectIds.map((id) => (
            <Badge key={id} variant="neutral">
              {nameOf(id)}
            </Badge>
          ))
        )}
        {editing && (
          <button
            type="button"
            className="text-sm text-link hover:underline"
            onClick={() => setPicking(true)}
          >
            Change
          </button>
        )}
        <span className="ml-auto flex gap-2">
          {canWrite && !editing && (
            <Button variant="secondary" onClick={() => setDraft(shown)}>
              Edit
            </Button>
          )}
          {canWrite &&
            !editing &&
            rule &&
            (rule.status === 'DRAFT' || rule.hasUnpublishedChanges) && (
              <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
                {publish.isPending ? 'Publishing…' : 'Publish'}
              </Button>
            )}
          {canWrite && !editing && rule && (
            <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
          {editing && (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  if (creating) void navigate('/knowledge/rules');
                  else setDraft(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={() => save.mutate()}
                disabled={busy || !draftReady}
              >
                {save.isPending ? 'Saving…' : 'Save draft'}
              </Button>
              <Button onClick={() => publishDraft.mutate()} disabled={busy || !draftReady}>
                {publishDraft.isPending ? 'Publishing…' : 'Publish'}
              </Button>
            </>
          )}
        </span>
      </div>
      {mutationError && <p className="text-sm text-danger">{(mutationError as Error).message}</p>}

      {editing ? (
        <div className="flex flex-col gap-4 rounded-lg border border-outline bg-surface-elevated p-6">
          <div>
            <label
              htmlFor="rule-title"
              className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-subtle"
            >
              Title
            </label>
            <Input
              id="rule-title"
              value={shown.title}
              onChange={(e) => setDraft({ ...shown, title: e.target.value })}
              placeholder="What is this rule about?"
              aria-label="Rule title"
              className="w-full"
            />
          </div>
          <div>
            <label
              htmlFor="rule-body"
              className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-subtle"
            >
              Body
            </label>
            <MarkdownEditor
              id="rule-body"
              value={shown.bodyMarkdown}
              onChange={(bodyMarkdown) => setDraft({ ...shown, bodyMarkdown })}
              disabled={busy}
              maxChars={RULE_BODY_MAX_CHARS}
            />
            {shown.bodyMarkdown.length > RULE_BODY_MAX_CHARS && (
              <p className="mt-2 text-sm text-danger">
                This is {(shown.bodyMarkdown.length - RULE_BODY_MAX_CHARS).toLocaleString()}{' '}
                characters over the limit for a rule. A rule is read on every question the assistant
                answers, so it has to stay short enough to be useful. If this is a manual or a
                product list, upload it as a document on the subject it belongs to instead — or
                split it into a few focused rules.
              </p>
            )}
            {extracted ? (
              <p className="mt-2 text-xs text-ink-muted">
                {extracted.text.length.toLocaleString()} extracted characters; the limit is{' '}
                {RULE_BODY_MAX_CHARS.toLocaleString()}. Keep the parts a mechanic would ask about.
              </p>
            ) : (
              <p className="mt-2 text-xs text-ink-muted">
                Name the equipment or system in your headings and throughout the text. The assistant
                finds answers by the words in them and may show one section on its own, so make each
                section clearly say what it’s about.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-outline bg-surface-elevated p-6">
          <MarkdownViewer>{shown.bodyMarkdown}</MarkdownViewer>
        </div>
      )}

      {confirmDelete && (
        <Modal open onOpenChange={(open) => !open && setConfirmDelete(false)}>
          <Modal.Header>
            <Modal.Title>Delete this rule?</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm text-ink-muted">
              The assistant stops using it straight away, and this can’t be undone. If you only want
              to stop using it for now, edit it instead — an unpublished rule isn’t used.
            </p>
            {remove.error && (
              <p className="mt-3 text-sm text-danger">{(remove.error as Error).message}</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Keep it
            </Button>
            <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? 'Deleting…' : 'Delete rule'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {picking && (
        <SubjectPickerModal
          subjects={subjects.data ?? []}
          selected={shown.subjectIds}
          onChange={(subjectIds) => setDraft({ ...shown, subjectIds })}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
};
